/** A note whose whole body is `user_note` (W47). One captured before W47 keeps its body in `content` and opens read-only. */
export const isNote = (nook) => nook.isNote && !nook.excerpt;
/** In the iOS serializer's nesting order: italic outermost, so `***x***` opens `*` then `**`. */
const MARKS = { italic: "*", bold: "**", strike: "~~" };
const ORDER = Object.keys(MARKS);
/** iOS caps nesting here (`kENRMMaxListDepth`). */
const MAX_DEPTH = 5;
/** A line that would read as another block: a heading, quote, list item, rule or setext underline. */
const BLOCKISH = /^(#{1,6}(\s|$)|>|[-+](\s|$)|-+\s*$|=+\s*$)/;
/** A line's text as a paragraph's markdown: its start escaped where it would read as another block. */
export const lineSafe = (text) => /^\d{1,9}[.)](\s|$)/.test(text) ? text.replace(/^(\d+)/, "$1\\") : BLOCKISH.test(text) ? `\\${text}` : text;
/** A typed character as markdown: anything either parser would read as syntax, escaped. `&` is escaped per line, in `inline`. */
const literal = (char) => char.replace(/[\\*_~`[<|]/, "\\$&");
const ENTITY = /(?<!\\)&(?=#?\w+;)/g;
/**
 * Markdown from elsewhere (a capture, a share, an older editor) in the form
 * the editors write: the syntax neither writes escaped, `*` and `~` kept as
 * marks, and an escape `literal` would not write dropped (`lineSafe` puts back
 * the ones a line start needs).
 */
const foreign = (text) => text.replace(/\\(?:([\\*_~`[<|])|([!-/:-@[-`{-~]))|[`_[<|]/g, (match, keep, drop) => keep ? match : drop ?? `\\${match}`).replace(ENTITY, "\\&");
/**
 * Runs to inline markdown, as `ENRMMarkdownSerializer` writes it: each mark's
 * runs trimmed of whitespace, closings before openings, outer marks first.
 */
export function inline(runs) {
    const chars = runs.flatMap((run) => [...run.text].map((char) => ({ char, marks: run.marks })));
    let start = 0;
    let end = chars.length;
    while (start < end && !chars[start].char.trim())
        start++;
    while (end > start && !chars[end - 1].char.trim())
        end--;
    const line = chars.slice(start, end);
    const open = line.map(() => []);
    const close = [...line, null].map(() => []);
    for (const mark of ORDER) {
        for (let i = 0; i < line.length;) {
            if (!line[i].marks.includes(mark)) {
                i++;
                continue;
            }
            let j = i;
            while (j < line.length && line[j].marks.includes(mark))
                j++;
            let a = i;
            let b = j;
            while (a < b && !line[a].char.trim())
                a++;
            while (b > a && !line[b - 1].char.trim())
                b--;
            if (a < b) {
                open[a].push(mark);
                close[b].unshift(mark);
            }
            i = j;
        }
    }
    let out = "";
    for (let i = 0; i <= line.length; i++) {
        out += close[i].map((mark) => MARKS[mark]).join("");
        if (i < line.length)
            out += open[i].map((mark) => MARKS[mark]).join("") + literal(line[i].char);
    }
    // An entity reads across characters, so `&` is escaped against the whole line.
    return out.replace(ENTITY, "\\&");
}
/** Any note body, canonical or not, as lines. Blank lines between blocks are TipTap's: every second one past the first is an empty paragraph. */
export function readNote(markdown) {
    const lines = [];
    let lists = [];
    let gap = 0;
    const empty = (count) => {
        for (let i = 0; i < count; i++)
            lines.push({ type: "p", depth: 0, text: "" });
        if (count > 0)
            lists = [];
    };
    for (const raw of markdown.replace(/\r\n?/g, "\n").split("\n")) {
        if (!raw.trim()) {
            gap++;
            continue;
        }
        empty(lines.length ? Math.floor((gap + 1) / 2) - 1 : Math.floor(gap / 2));
        gap = 0;
        const item = raw.match(/^(\s*)([-*+]|\d{1,9}[.)])(?:\s+(.*))?$/);
        const heading = raw.trim().match(/^#{1,6}(?:\s+(.*?))?(?:\s+#+)?$/);
        if (item) {
            const indent = item[1].replace(/\t/g, "    ").length;
            while (lists.length) {
                const top = lists.at(-1);
                if (indent >= top.content)
                    break;
                lists.pop();
                if (indent >= top.indent)
                    break;
            }
            const depth = Math.min(lists.length, MAX_DEPTH);
            lists.push({ indent, content: indent + item[2].length + 1 });
            lines.push({ type: /\d/.test(item[2]) ? "ol" : "ul", depth, text: foreign(item[3]?.trim() ?? "") });
            continue;
        }
        lists = [];
        if (raw.trim() === "&nbsp;")
            lines.push({ type: "p", depth: 0, text: "" });
        else
            lines.push({ type: heading ? "h" : "p", depth: 0, text: foreign(heading ? (heading[1] ?? "") : raw.trim()) });
    }
    empty(lines.length ? Math.floor(gap / 2) : 0);
    return lines;
}
/**
 * Lines to markdown: the stored note, or with `ios` the iOS editor's own form,
 * one line each. Numbered and nested as iOS shows them: an item at most one
 * level under the item above, numbers counted from 1 per run, and an empty
 * item an empty paragraph, as iOS saves one. Empty lines at either end are
 * dropped, as the save's `trim()` always dropped them, so an emptied note is "".
 */
export function writeNote(all, { ios = false } = {}) {
    const kept = all.map((line) => line.text.trim() !== "");
    const lines = all.slice(kept.indexOf(true), kept.lastIndexOf(true) + 1);
    let previous = -1;
    const counters = [];
    const types = [];
    const columns = [];
    const out = lines.map((line) => {
        const text = line.text.trim();
        if (!text || line.type === "p" || line.type === "h") {
            previous = -1;
            return !text ? (ios ? "" : "&nbsp;") : line.type === "h" ? `## ${lineSafe(text).replace(/(^|\s)(#+)$/, "$1\\$2")}` : lineSafe(text);
        }
        const depth = Math.min(line.depth, previous + 1, MAX_DEPTH);
        if (previous < 0)
            types.length = 0;
        counters.length = types.length = columns.length = depth + 1;
        if (types[depth] !== line.type) {
            counters[depth] = 0;
            types[depth] = line.type;
        }
        const marker = line.type === "ol" ? `${(counters[depth] = (counters[depth] ?? 0) + 1)}.` : "-";
        const indent = depth ? columns[depth - 1] : 0;
        columns[depth] = indent + marker.length + 1;
        previous = depth;
        return `${" ".repeat(indent)}${marker} ${lineSafe(text)}`;
    });
    return ios ? out.join("\n") : out.reduce((note, line, i) => (i ? note + (isItem(line) && isItem(out[i - 1]) ? "\n" : "\n\n") + line : line), "");
}
const isItem = (line) => /^\s*(-|\d+\.) /.test(line);
/** Any note body to the stored form. Idempotent: a stored note comes back unchanged. */
export const normalizeNote = (markdown) => writeNote(readNote(markdown));
/** A stored note to the iOS editor's markdown: one block per line, nothing between. */
export const noteToBlocks = (markdown) => writeNote(readNote(markdown), { ios: true });
/**
 * The iOS editor's markdown back to the stored form. Its serializer escapes
 * nothing, so a typed `*` or a line starting `- ` would come back as syntax;
 * the editor's plain text, line for line, tells the typed characters from the
 * marks. `active` carries a mark across lines, as iOS writes one.
 */
export function blocksToNote(markdown, plain) {
    const lines = markdown.split("\n");
    const texts = plain.split("\n");
    // Unreachable while iOS keeps its line-count invariant; read as markdown from elsewhere, so a typed `*` may come back a mark.
    if (lines.length !== texts.length)
        return normalizeNote(markdown);
    const active = new Set();
    const out = [];
    for (const [i, line] of lines.entries()) {
        const text = texts[i];
        const block = line.match(/^( *)(- |\d+\. |#{1,6} )/);
        // A prefix the plain text also has was typed, not a block.
        const rest = block && line.length - block[0].length >= text.length && runsOf(line.slice(block[0].length), text, new Set(active)) ? line.slice(block[0].length) : null;
        const runs = runsOf(rest ?? line, text, active);
        if (!runs)
            return normalizeNote(markdown);
        const marker = rest === null ? null : block[2];
        out.push({
            type: !marker ? "p" : marker.startsWith("#") ? "h" : marker === "- " ? "ul" : "ol",
            depth: marker ? Math.floor(block[1].length / 3) : 0,
            text: inline(runs),
        });
    }
    return writeNote(out);
}
/**
 * The marks on `text` from its markdown, toggling a mark per delimiter; `null`
 * if they do not line up. A delimiter the text also has is a mark only if the
 * rest of the text can still follow it.
 */
function runsOf(markdown, text, active) {
    const runs = [];
    let j = 0;
    const link = /\]\([^)]*\)/y;
    for (let i = 0; i < markdown.length;) {
        const typed = j < text.length && markdown[i] === text[j];
        link.lastIndex = i;
        // Underline, spoiler and link come from a paste; a note keeps their text, not the mark.
        const token = [link.exec(markdown)?.[0], "**", "~~", "*", "_", "||", "["].find((delimiter) => delimiter && markdown.startsWith(delimiter, i) && (!typed || follows(markdown, i + delimiter.length, text, j)));
        if (token) {
            const mark = ORDER.find((m) => MARKS[m] === token);
            if (mark)
                active[active.has(mark) ? "delete" : "add"](mark);
            i += token.length;
            continue;
        }
        if (!typed)
            return null;
        const marks = ORDER.filter((mark) => active.has(mark));
        const last = runs.at(-1);
        if (last && last.marks.join() === marks.join())
            last.text += text[j];
        else
            runs.push({ text: text[j], marks });
        i++;
        j++;
    }
    return j === text.length ? runs : null;
}
/** Whether `text` from `j` is a subsequence of `markdown` from `i`. */
function follows(markdown, i, text, j) {
    for (; i < markdown.length && j < text.length; i++)
        if (markdown[i] === text[j])
            j++;
    return j === text.length;
}
//# sourceMappingURL=note.js.map